import { Autocomplete, FormControl, FormLabel } from '@mui/joy';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type RouterOutputs, trpc } from '@/config/trpc';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';
import AddCaseActions from '../AddEditCase/AddCaseActions';
import { CaseType } from '~/common/enums';
import CommonFormBody from '../AddEditCase/CommonFormBody';
import { type DefaultFormValues } from '../AddEditCase/Config/FormValues';
import { getOpenCaseInNewTabValue } from '@/shared/utils/helpers';
import { InlineSpinner } from '@/shared/icons/Icons';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';
import TemplateAlerts from '../AddEditCase/TemplateAlerts';
import useAddCaseFormConfig from '../AddEditCase/Config/useAddCaseFormConfig';
import useGetCaseTemplate from '@/queries/cases/useGetCaseTemplate';

type CaseTemplateOption = RouterOutputs['team']['case']['template']['autocomplete'][0];

type Props = {
  toggle: UseToggle;
  initialData?: DefaultFormValues;
  callback?: (caseId: string) => void;
};

const AddSalesCase: React.FC<Props> = ({ toggle, initialData, callback }) => {
  const { t } = useTranslation();

  const [selectedTemplate, setSelectedTemplate] = useState<CaseTemplateOption | null>(null);
  const { currentCaseTemplate, currentCaseTemplateIsFetching } = useGetCaseTemplate(
    selectedTemplate?.value,
  );

  const { data: settings } =
    trpc.messaging.pushNotificationSettings.getBuildingPortalPushNotificationSettings.useQuery();

  const { data: caseTemplates, isLoading: isLoadingCaseTemplates } =
    trpc.team.case.template.autocomplete.useQuery({
      isManual: true,
    });

  const toggles = {
    openCaseInNewTab: useToggle(getOpenCaseInNewTabValue()),
    pushNotification: useToggle(settings?.caseAdded),
    redirect: useToggle(!getOpenCaseInNewTabValue()),
    showOnPortal: useToggle(),
    task: useToggle(),
  };

  const { initialValues, validationSchema, handleSubmit, isCreatingCase } = useAddCaseFormConfig({
    callback,
    caseType: CaseType.Sales,
    initialData,
    templateId: selectedTemplate?.value,
    toggle,
    toggles,
  });

  return (
    <KpcModal
      className="max-h-[80vh] w-4/5 overflow-auto 2xl:w-2/3"
      title={t('_salesCases.forms.create.title')}
      toggle={toggle}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form className="flex flex-col space-y-4">
          <FormControl>
            <FormLabel>{t('masterCaseTemplate')}</FormLabel>
            <Autocomplete
              loading={isLoadingCaseTemplates}
              endDecorator={currentCaseTemplateIsFetching && <InlineSpinner />}
              placeholder={t('search.placeholder')}
              options={caseTemplates ?? []}
              isOptionEqualToValue={(option, selectedOption) =>
                option.value === selectedOption.value
              }
              onChange={(_e, option) => {
                if (option) {
                  setSelectedTemplate(option);
                } else {
                  setSelectedTemplate(null);
                }
              }}
            />
          </FormControl>

          <CommonFormBody
            taskToggle={toggles.task}
            caseType={CaseType.Sales}
            currentCaseTemplate={currentCaseTemplate}
          />

          <TemplateAlerts template={currentCaseTemplate} toggle={toggle} />

          <AddCaseActions
            toggles={{
              modal: toggle,
              ...toggles,
            }}
            isMutating={isCreatingCase}
          />
        </Form>
      </Formik>
    </KpcModal>
  );
};

export default AddSalesCase;
